import React from "react";
import GenesysChatbot from "../GenesysChatbot";
import useParentBrand from "./useParentBrand";
import { sendChatEventToHost } from "./hostProtocol";
import type { Brand, ChatbotConfig, Environment } from "../lib/ChatbotConfig";

interface StandaloneChatbotProps {
  /** Brand from the build — only a fallback, the host page knows better. */
  brand?: Brand;
  environment?: Environment;
}

/**
 * The widget as it is deployed into the chat iframe.
 *
 * Everything iframe-specific lives here, so the npm package stays free of it:
 * the brand comes from the embedding page, analytics events go to the host's
 * dataLayer via postMessage, and the host bridge keeps the iframe the size of
 * the widget.
 */
const StandaloneChatbot = ({
  brand,
  environment,
}: StandaloneChatbotProps): React.JSX.Element => {
  const { brand: parentBrand, settled } = useParentBrand();

  const config: ChatbotConfig = React.useMemo(
    () => ({
      // The host page wins: one deployment serves all brands, so the build-time
      // brand is a guess and the embedding domain is fact.
      brand: parentBrand ?? (brand as Brand),
      environment: environment as Environment,
      onChatEvent: sendChatEventToHost,
    }),
    [parentBrand, brand, environment],
  );

  // Nothing is visible before the host bridge has sized the iframe anyway, so
  // waiting out the handshake costs nothing — and it avoids mounting with the
  // wrong brand, which would flash the wrong theme and trip the config guard.
  if (!settled) return <React.Fragment />;

  return <GenesysChatbot config={config} hostBridge />;
};

export default StandaloneChatbot;
